import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  Box,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListSubheader,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ROUTE_PATH from '../../../config/routes.config';
import Iconify from '../../common/iconify.component';
import {
  ADMIN_USER_ROLE,
  CONTENT_ALIAS,
  FOCUS_PALETTE_BGCOLOR,
  SHOW_ADMIN_USERS,
  SHOW_CONTENT_CHANNELS,
  SHOW_CONTENT_VIDEOS,
  SHOW_CONTENT_CATEGORIES,
  SHOW_OVERVIEW_DASHBOARD,
  SHOW_REPORTS_OVERVIEW_VIDEOS,
  SHOW_REPORTS_WATCH_SESSIONS,
  SHOW_REPORTS_APP_SESSIONS,
  SHOW_COLLECTION,
} from '../../../config/const.config';
import palette from '../../../theme/palette';
import { PRIMARY } from '../../../theme/theme-colors';

const StyledSubHeader = styled((props) => <ListSubheader {...props} />)(
  ({ theme }) => ({
    background: 'transparent',
    color: theme.palette.grey[200],
  })
);

const StyledNavItem = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
}));

const StyledNavItemIcon = styled(ListItemIcon)({
  width: 24,
  height: 24,
  color: palette.common.black,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
});

const NavItem = ({ title, path, icon, subNav }) => (
  <StyledNavItem
    component={RouterLink}
    to={path}
    sx={{
      '&:hover': {
        color: palette.common.black,
        bgcolor: FOCUS_PALETTE_BGCOLOR,
      },
      '&.active': {
        color: palette.common.black,
        bgcolor: PRIMARY.main,
        fontWeight: 'fontWeightBold',
        '.MuiListItemText-root': {
          color: 'black',
        },
        '.MuiBox-root': {
          color: 'black',
        },
      },
      height: subNav ? 36 : 48,
    }}
  >
    {!subNav && <StyledNavItemIcon>{icon}</StyledNavItemIcon>}
    <ListItemText
      disableTypography
      primary={title}
      sx={{
        color: palette.common.black,
        pl: subNav ? 6 : 0,
        overflowX: 'clip',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
        paddingRight: 2,
      }}
    />
  </StyledNavItem>
);
NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.element,
  subNav: PropTypes.bool,
};
NavItem.defaultProps = {
  icon: <div />,
  subNav: false,
};

const NavGroup = ({ title, routePath, icon, navGroup }) => {
  const urlParts = window.location.href.split('/');
  const routePathParts = routePath.split('/');

  const initOpen =
    urlParts[urlParts.length - 2] === routePathParts[routePathParts.length - 1];

  const [open, setOpen] = useState(initOpen);
  const handleClick = () => setOpen(!open);

  return (
    <>
      <StyledNavItem onClick={handleClick}>
        <StyledNavItemIcon>{icon}</StyledNavItemIcon>
        <ListItemText
          disableTypography
          primary={title}
          sx={{
            color: palette.common.black,
            overflowX: 'clip',
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        />
        <StyledNavItemIcon>
          <Iconify icon={open ? 'mdi:menu-up' : 'mdi:menu-down'} />
        </StyledNavItemIcon>
      </StyledNavItem>

      <Collapse in={open} timeout="auto">
        <List disablePadding sx={{ p: 1 }}>
          {navGroup.map(
            (nav) =>
              nav.status === 1 && (
                <NavItem
                  key={nav.title}
                  title={nav.title}
                  path={`${routePath}/${nav.slug}`}
                  icon={<div />}
                  subNav
                />
              )
          )}
        </List>
      </Collapse>
    </>
  );
};
NavGroup.propTypes = {
  title: PropTypes.string.isRequired,
  routePath: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  navGroup: PropTypes.array.isRequired,
};

const NavItems = ({ adminUserRole }) => (
  <Box>
    <List disablePadding sx={{ p: 1 }}>
      {SHOW_OVERVIEW_DASHBOARD && (
        <NavItem
          title="Overview Dashboard"
          path={ROUTE_PATH.DASHBOARD}
          icon={<Iconify icon="ic:round-dashboard" />}
        />
      )}

      <StyledSubHeader component="div">Content</StyledSubHeader>

      {SHOW_CONTENT_CHANNELS && (
        <NavItem
          title="Channels"
          path={ROUTE_PATH.CONTENT_CHANNELS}
          icon={<Iconify icon="ic:twotone-checklist" />}
        />
      )}
      {SHOW_COLLECTION && (
        <NavItem
          title="Collections"
          path={ROUTE_PATH.CONTENT_COLLECTIONS}
          icon={<Iconify icon="material-symbols:tab-group-outline" />}
        />
      )}
      {SHOW_CONTENT_CATEGORIES && (
        <NavItem
          title="Categories"
          path={ROUTE_PATH.CONTENT_CATEGORIES}
          icon={<Iconify icon="iconamoon:category" />}
        />
      )}

      {SHOW_CONTENT_VIDEOS && (
        <NavItem
          title={`${CONTENT_ALIAS.VIDEO_PLURAL}`}
          path={ROUTE_PATH.CONTENT_VIDEOS}
          icon={<Iconify icon="octicon:video-16" />}
        />
      )}

      {(SHOW_REPORTS_OVERVIEW_VIDEOS ||
        SHOW_REPORTS_WATCH_SESSIONS ||
        SHOW_REPORTS_APP_SESSIONS) && (
        <StyledSubHeader component="div">Analytics Reports</StyledSubHeader>
      )}
      {SHOW_REPORTS_OVERVIEW_VIDEOS && (
        <NavItem
          title={`Overview (${CONTENT_ALIAS.VIDEO_PLURAL})`}
          path={ROUTE_PATH.REPORTS_OVERVIEW_VIDEOS}
          icon={<Iconify icon="ic:outline-analytics" />}
        />
      )}
      {SHOW_REPORTS_WATCH_SESSIONS && (
        <NavItem
          title="Watch Sessions"
          path={ROUTE_PATH.REPORTS_WATCH_SESSIONS}
          icon={<Iconify icon="ic:outline-list-alt" />}
        />
      )}
      {SHOW_REPORTS_APP_SESSIONS && (
        <NavItem
          title="App Sessions"
          path={ROUTE_PATH.REPORTS_APP_SESSIONS}
          icon={<Iconify icon="ic:outline-list-alt" />}
        />
      )}

      {SHOW_ADMIN_USERS && adminUserRole === ADMIN_USER_ROLE.ADMIN && (
        <StyledSubHeader component="div">User Management</StyledSubHeader>
      )}
      {SHOW_ADMIN_USERS && adminUserRole === ADMIN_USER_ROLE.ADMIN && (
        <NavItem
          title="CMS Users"
          path={ROUTE_PATH.ADMIN_USERS}
          icon={<Iconify icon="mdi:users-check" />}
        />
      )}
    </List>
  </Box>
);

NavItems.propTypes = {
  adminUserRole: PropTypes.string.isRequired,
};

export default NavItems;
